const PROJECT_TYPES = new Set(['assignment', 'list', 'opportunity']);

/**
 * Validate depth value: must be a non-negative integer.
 * minDepth: 0 for non-project modals, 1 for stored project depths.
 */
const validateDepth = (value, minDepth = 0) => {
    const parsedDepth = Number(value);
    return Number.isInteger(parsedDepth) && parsedDepth >= minDepth ? parsedDepth : null;
};

const extractProjectType = (modalData) => {
    const data = modalData.data || modalData;
    const dataType = data?.dataType;
    return dataType ? dataType.singular() : null;
};

export const isProjectModal = (modalData) => {
    const projectType = extractProjectType(modalData);
    return PROJECT_TYPES.has(projectType);
};

/**
 * Resolve the depth level for a modal context.
 * Returns null for non-project modals (e.g., person in project).
 */
export const resolveProjectCandidatesModalDepth = (modal) => {
    if (!modal?.showModal) {
        return null;
    }

    const currentDepth = validateDepth(modal.data?.projectCandidatesScopeDepth, 1);
    if (currentDepth) {
        return currentDepth;
    }

    const isCurrentProject = isProjectModal(modal.data);

    if (Array.isArray(modal.backtrace)) {
        return isCurrentProject ? 1 : 0;
    }

    return null;
};

/**
 * Calculate and return scoped store ID from a project ID and depth.
 * Handles naming: 'projectCandidates' for main, 'projectCandidates_{depth}' for nested.
 */
export const createProjectCandidatesScopeId = (projectId, depth) => {
    if (!depth || depth === 0) {
        return 'projectCandidates';
    }
    return projectId ? `${projectId}_${depth}` : `projectCandidates_${depth}`;
};

/**
 * Resolve project candidates scope ID.
 * For modals, uses depth-based naming; for page level, uses 'projectCandidates'.
 * Modal depth starts at 1 for project modals, 0 for non-project modals, and increments for nested projects.
 */
export const resolveProjectCandidatesScopeId = ({
    isInModal = false,
    modalDepth = null,
} = {}) => {
    const depth = validateDepth(modalDepth, 0);
    const validDepth = depth !== null ? depth : (isInModal ? 1 : 0);

    // Scope IDs must stay depth-based to match registered store IDs
    // (projectCandidates, projectCandidates_1, projectCandidates_2, ...)
    return createProjectCandidatesScopeId(null, validDepth);
};

/**
 * Normalize project type to one of the allowed types.
 * Returns the normalized type or null if not an allowed project type.
 */
export const normalizeProjectType = (projectType = null) => {
    const type = projectType ? String(projectType).toLowerCase() : null;
    return PROJECT_TYPES.has(type) ? type : null;
};

/**
 * Get store ID from depth level (wrapper for backwards compatibility).
 * Returns 'projectCandidates' for main store, 'projectCandidates_{depth}' for nested.
 */
export const getStoreIdFromDepth = (depth) => {
    return createProjectCandidatesScopeId(null, depth);
};

export const getDepthFromStoreId = (storeId) => {
    if (!storeId || typeof storeId !== 'string') {
        return null;
    }
    const match = /^projectCandidates(?:_(\d+))?$/.exec(storeId);
    if (match) {
        const depth = Number(match[1] || '0');
        return Number.isInteger(depth) && depth >= 0 ? depth : null;
    }
    return null;
};

/**
 * Resolve the depth for opening a child project modal.
 * For project modals: increments inherited depth or starts at 1.
 * For non-project modals (person): only inherits existing depth.
 * @param {Object} parentModalData - Parent modal data object
 * @param {Object} options - { isProjectType: boolean }
 * @returns {number|null} Depth level or null if no depth should be set
 */
export const resolveNextProjectCandidatesDepth = (parentModalData, options = {}) => {
    const { isProjectType = false } = options;

    const inheritedDepth = validateDepth(parentModalData?.projectCandidatesScopeDepth, 1);

    if (!isProjectType) {
        return inheritedDepth;
    }

    if (inheritedDepth) {
        return inheritedDepth + 1;
    }

    return 1;
};
